<div class="image-upload-container">
  <div class="upload-section">
    <h3>
      <svg viewBox="0 0 24 24" class="section-icon" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21,15 16,10 5,21" />
      </svg>
      Upload Images
      <span class="optional-label" *ngIf="!required">(Optional)</span>
    </h3>
    <p class="upload-description">
      Add up to {{ maxImages }} high-quality images to showcase your listing.
      Supports the majority of image types. Drag to reorder. Max
      {{ maxSizeMB }}MB per photo.
    </p>

    <div
      class="file-drop-zone"
      role="button"
      tabindex="0"
      [class.dragover]="isDragOver"
      [class.disabled]="isUploading || images.length >= maxImages"
      [attr.aria-label]="
        'Upload images. ' + getRemainingSlots() + ' slots remaining'
      "
      [attr.aria-disabled]="isUploading || images.length >= maxImages"
      (click)="triggerFileSelect()"
      (keydown)="handleKeyboardTrigger($event)"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave($event)"
      (drop)="onDrop($event)"
    >
      <input
        #fileInput
        type="file"
        multiple
        [attr.accept]="getAcceptAttribute()"
        [attr.aria-label]="'Select images to upload'"
        (change)="onFileSelect($event)"
        style="display: none"
      />

      <div class="drop-zone-content" *ngIf="images.length < maxImages">
        <div class="upload-icon-wrapper">
          <svg class="upload-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
            />
            <path d="M12,11L16,15H13V19H11V15H8L12,11Z" />
          </svg>
          <div class="upload-plus" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </div>
        </div>
        <div class="drop-zone-text">
          <h4>
            {{
              isDragOver
                ? "Drop images here"
                : "Click to upload or drag and drop"
            }}
          </h4>
          <p>
            {{ getRemainingSlots() }} image{{
              getRemainingSlots() !== 1 ? "s" : ""
            }}
            remaining
          </p>
        </div>
      </div>

      <div class="upload-limit-reached" *ngIf="images.length >= maxImages">
        <div class="limit-icon-wrapper">
          <svg class="limit-icon" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        </div>
        <div class="limit-text">
          <h4>Maximum images reached</h4>
          <p>Remove some images to add new ones</p>
        </div>
      </div>
    </div>

    <div
      class="upload-errors"
      *ngIf="uploadErrors.length > 0"
      role="alert"
      aria-live="polite"
    >
      <div
        class="error-item"
        *ngFor="let error of uploadErrors; trackBy: trackByIndex"
      >
        <svg class="error-icon" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span>{{ error }}</span>
      </div>
    </div>
  </div>

  <div
    class="images-grid"
    *ngIf="images.length > 0"
    role="list"
    aria-label="Uploaded images"
  >
    <div
      class="image-item"
      role="listitem"
      *ngFor="let image of images; let i = index; trackBy: trackByImage"
      [class.uploading]="image.uploading"
      [class.uploaded]="image.uploaded"
      [class.error]="image.error"
      [class.dragging]="draggedIndex === i"
      [class.drag-over]="dragOverIndex === i"
      [class.limited-support]="hasLimitedBrowserSupport(image)"
      [class.converted]="wasImageConverted(image)"
      [attr.aria-label]="
        'Image ' + (i + 1) + ' of ' + images.length + ': ' + image.file.name
      "
      draggable="true"
      (dragstart)="onImageDragStart($event, i)"
      (dragover)="onImageDragOver($event, i)"
      (dragenter)="onImageDragEnter($event, i)"
      (dragleave)="onImageDragLeave($event)"
      (drop)="onImageDrop($event, i)"
      (dragend)="onImageDragEnd($event)"
    >
      <div class="image-preview">
        <img
          *ngIf="image.preview"
          [src]="image.preview"
          [alt]="'Preview of ' + image.file.name"
          loading="lazy"
        />

        <div class="heic-placeholder" *ngIf="image.isHEIC && !image.preview">
          <div class="heic-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21,15 16,10 5,21" />
            </svg>
          </div>
          <div class="heic-text">
            <h4>HEIC Picture</h4>
            <p>Uploading your photo...</p>
          </div>
        </div>

        <div
          class="drag-handle"
          *ngIf="!image.uploading && images.length > 1"
          [attr.aria-label]="'Drag to reorder image ' + (i + 1)"
          role="button"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="9" cy="12" r="1" />
            <circle cx="9" cy="5" r="1" />
            <circle cx="9" cy="19" r="1" />
            <circle cx="15" cy="12" r="1" />
            <circle cx="15" cy="5" r="1" />
            <circle cx="15" cy="19" r="1" />
          </svg>
        </div>

        <div
          class="upload-overlay"
          *ngIf="image.uploading"
          role="status"
          aria-live="polite"
        >
          <div class="upload-spinner" aria-hidden="true">
            <div class="spinner"></div>
          </div>
          <p>Uploading...</p>
        </div>

        <div
          class="success-badge"
          *ngIf="image.uploaded && !image.error"
          aria-label="Upload successful"
        >
          <svg class="success-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>

        <div class="error-badge" *ngIf="image.error" aria-label="Upload failed">
          <svg class="error-icon" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        <div class="image-number" aria-hidden="true">{{ i + 1 }}</div>
      </div>

      <div class="image-actions">
        <button
          type="button"
          class="action-btn rotate"
          (click)="rotateImage(i, -90)"
          [disabled]="isUploading || rotatingImageIndex === i"
          [attr.aria-label]="'Rotate image ' + (i + 1) + ' counter-clockwise'"
          title="Rotate left"
          *ngIf="!image.uploading"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M1 4v6h6" />
            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
          </svg>
        </button>

        <button
          type="button"
          class="action-btn rotate"
          (click)="rotateImage(i, 90)"
          [disabled]="isUploading || rotatingImageIndex === i"
          [attr.aria-label]="'Rotate image ' + (i + 1) + 'clockwise'"
          title="Rotate right"
          *ngIf="!image.uploading"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M23 4v6h-6" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
        </button>

        <button
          type="button"
          class="action-btn danger"
          (click)="removeImage(i)"
          [disabled]="isUploading"
          [attr.aria-label]="'Remove image ' + (i + 1) + ': ' + image.file.name"
          title="Remove image"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <polyline points="3,6 5,6 21,6" />
            <path
              d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"
            />
          </svg>
        </button>
      </div>

      <div class="image-info">
        <p class="image-name">{{ image.file.name }}</p>
        <p class="image-size">{{ formatFileSize(image.file.size) }}</p>
      </div>
    </div>
  </div>

  <div class="upload-actions" *ngIf="images.length > 0">
    <div class="upload-summary">
      <div class="summary-stats">
        <span class="images-count" role="status" aria-live="polite">
          {{ getUploadedCount() }} of {{ images.length }} images uploaded
        </span>
        <span class="total-size">Total size: {{ getTotalSize() }}</span>
      </div>
      <div
        class="upload-progress"
        *ngIf="!allImagesUploaded() && images.length > 0"
        role="progressbar"
        [attr.aria-valuenow]="getUploadedCount()"
        [attr.aria-valuemin]="0"
        [attr.aria-valuemax]="images.length"
        [attr.aria-label]="
          'Upload progress: ' +
          getUploadedCount() +
          ' of ' +
          images.length +
          ' images'
        "
      >
        <div class="progress-circle">
          <svg class="progress-ring" width="24" height="24" aria-hidden="true">
            <circle
              class="progress-ring-circle"
              stroke="currentColor"
              stroke-width="2"
              fill="transparent"
              r="10"
              cx="12"
              cy="12"
              [style.stroke-dasharray]="2 * Math.PI * 10"
              [style.stroke-dashoffset]="
                2 * Math.PI * 10 * (1 - getUploadedCount() / images.length)
              "
            />
          </svg>
        </div>
      </div>
    </div>

    <div class="action-buttons">
      <button
        type="button"
        class="btn btn-secondary"
        (click)="clearAllImages()"
        [disabled]="isUploading"
        aria-label="Clear all images"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <polyline points="3,6 5,6 21,6" />
          <path
            d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"
          />
        </svg>
        Clear All
      </button>

      <button
        type="button"
        class="btn btn-primary"
        (click)="uploadAllImages()"
        [disabled]="isUploading || allImagesUploaded()"
        [attr.aria-label]="
          isUploading ? 'Uploading images' : 'Upload all images'
        "
        *ngIf="!allImagesUploaded()"
      >
        <span *ngIf="!isUploading">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7,10 12,15 17,10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Upload All Images
        </span>
        <span *ngIf="isUploading" class="uploading-text">
          <div class="inline-spinner" aria-hidden="true"></div>
          Uploading...
        </span>
      </button>

      <div
        class="upload-complete"
        *ngIf="allImagesUploaded() && images.length > 0"
        role="status"
        aria-live="polite"
      >
        <svg class="complete-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <span>All images uploaded successfully!</span>
      </div>
    </div>
  </div>
</div>
